/**
 * F-131 — Prime Score + Asset Roadmap: canonical constants & types.
 *
 * Implements the dual-BPS model from ALFIE Diagnostic System **v3.3**
 * (fb-alfie-diagnostic-system.md, 2026-Jul-04 — supersedes the v3.2 docx and
 * the xlsx DUAL_BPS_CALCULATION sheet where they conflict):
 *
 *   BPS = Asset Health (max 600) + KPI Health (max 400)  →  0–1000  (additive, always)
 *
 * Assets carry a priority ORDER (sequencing) and a 1–5 MATURITY (strength).
 * There are no per-asset weights — Pillar Asset Health is the ZERO-BASED
 * average maturity ((avg − 1) / 4), normalised to the pillar's max points,
 * so "Not in place" earns 0 credit (v3.3 §4.2).
 *
 * The score is computed ONCE, on the server (hermes `_shared/prometheus/roadmap-score.ts`,
 * 9 Sep 2026) and read as a stored result; these are the vocabulary and the view shapes the
 * screens render from it. Ids are the real `assets.id` uuids.
 */

import type { MoveResponseState } from '@/lib/roadmap/suggested-moves';
import type { Metric } from '@/types/tracking-view';

export const PILLARS = [
  'finance',
  'operations',
  'management',
  'marketing',
  'sales',
  'culture',
] as const;

export type PillarCode = (typeof PILLARS)[number];

export const PILLAR_LABELS: Record<PillarCode, string> = {
  finance: 'Finance',
  operations: 'Operations',
  management: 'Management',
  marketing: 'Marketing',
  sales: 'Sales',
  culture: 'Culture',
};


export const ASSET_HEALTH_MAX = 600;
export const KPI_HEALTH_MAX = 400;
export const BPS_MAX = 1000;



// KPIs are measured (not built): a traffic-light vs benchmark, not a 1–5 maturity.
export type KpiStatus = 'green' | 'amber' | 'red' | 'not_connected';

export const KPI_STATUS_LABELS: Record<KpiStatus, string> = {
  green: 'On target',
  amber: 'Below target',
  red: 'Off target',
  not_connected: 'Not connected',
};


/** Maturity scale (1–5), canonical v3.3 labels — these carry the rubric semantics
 * (2 = exists only in the owner's head; 3 = written down but inconsistently followed). */
export const MATURITY_LABELS: Record<number, string> = {
  1: 'Not in place',
  2: 'Informal',
  3: 'Documented',
  4: 'Implemented',
  5: 'Optimised',
};

/** A maturity at or above this is considered "in place" / prerequisite-satisfying. */
export const IN_PLACE_THRESHOLD = 3;

export type AssetState = 'strong' | 'developing' | 'available' | 'locked';

export const ASSET_STATE_LABELS: Record<AssetState, string> = {
  strong: 'Strong',
  developing: 'In progress',
  available: 'Ready to build',
  locked: 'Locked',
};

// ── Raw data shapes (from the mock / backend) ────────────────────────────────

/**
 * One line of a point's rubric (`asset_rubric_levels`, served with the roadmap points): what
 * level n is called and what it looks like. The catalogue carries levels 2–5; level 1 has no
 * row, so the fallback below names it.
 */
export interface RubricLevel {
  level: number;
  label: string;
  description: string;
}

export interface CatalogueAsset {
  id: string;
  pillar: PillarCode;
  orderIndex: number;
  name: string;
  prerequisiteOrder: number; // within-pillar # ref; 0 = none
  prerequisiteId: string | null;
  description: string;
  roles: string[];
  /** The point's own rubric, when the points payload carried it. Empty/absent is normal. */
  rubric?: RubricLevel[];
}

export type MaturityMap = Record<string, number>; // assetId → 1–5

export interface CatalogueKpi {
  id: string;
  pillar: PillarCode;
  name: string;
  unit: string;
  target: string; // benchmark, e.g. "≥30% (resi builder)"
  dataSource: string; // e.g. "Xero / Accounting"
  frequency: string; // e.g. "Monthly"
  constraintSignal: string; // what breaching it triggers
}

export interface KpiStateEntry {
  status: KpiStatus;
  value: string | null; // measured value display, null when not_connected
}
export type KpiStateMap = Record<string, KpiStateEntry>; // kpiId → state

/** A course finished by someone in the org during a diagnostic period. */
export interface CourseCompletion {
  name: string;
  completedBy: string; // display name + role, e.g. "Jake — Site Foreman"
}


// ── Computed shapes (engine output) ──────────────────────────────────────────

export interface RoadmapAsset extends CatalogueAsset {
  maturity: number; // 1–5
  maturityLabel: string;
  state: AssetState;
  prerequisiteName: string | null;
  prerequisiteMet: boolean;
}

export interface RoadmapKpi extends CatalogueKpi {
  /** The light when the figure counts, else not_connected (the row is greyed in place). */
  status: KpiStatus;
  statusLabel: string;
  /** The formatted figure, or null when there is none. */
  value: string | null;
  /** Whether the figure earns credit: ok data quality, not approximate, a traffic light. */
  counted: boolean;
  /** The cockpit-shaped metric behind the row, for the confidence signposting; null when none. */
  metric: Metric | null;
  /** The catalogue code (KPI_FIN_001…); the metric id for a pre-catalogue row. */
  code: string;
  /** The platform code that measures it, or null when none is named. */
  platform: string | null;
  /** A live platform measures this row (Xero today). */
  measured: boolean;
  /** Why an unmeasured row is greyed, in the builder's words; null when measured. */
  greyedReason: string | null;
  /** The platform's display name (Xero, Wunderbuild…), or null when none is named. */
  platformName: string | null;
  /**
   * A named platform will measure this row but is not wired yet (status `planned`): the Results
   * track lists these first, greyed, under a "Coming soon · <platform>" line (Tim, 10 Sep 2026).
   */
  coming: boolean;
}

export interface PillarRoadmap {
  pillar: PillarCode;
  label: string;
  pillarScore: number; // 0–100, derived from points: (AH + KH) / (AH_max + KH_max) × 100 — single source of truth
  // Systems track (Asset Health)
  assets: RoadmapAsset[];
  assetHealthPoints: number; // 0 → PILLAR_AH_MAX (zero-based maturity credit)
  assetHealthMax: number;
  inPlaceCount: number; // maturity ≥ IN_PLACE_THRESHOLD (3 = Documented)
  totalCount: number;
  // Results track (KPI Health)
  kpis: RoadmapKpi[];
  kpiHealthPoints: number; // 0 → PILLAR_KPI_MAX
  kpiHealthMax: number;
  kpiCountedCount: number; // rows whose figure counts
  kpiTotalCount: number;
}

export type ConstraintPriority = 'P0' | 'P1' | 'P2' | 'P3' | 'P4' | 'P5';

/** Canonical P-level categories (v3.3 §10.1). P0/P1 come only from triage +
 * guardrails (not implemented in this slice) — the score-driven engine emits
 * P2–P5 by pillar category. */
export const CONSTRAINT_PRIORITY_LABELS: Record<ConstraintPriority, string> = {
  P0: 'Survival',
  P1: 'Guardrails',
  P2: 'Financial',
  P3: 'Operational',
  P4: 'Growth',
  P5: 'People',
};


export interface Constraint {
  pillar: PillarCode;
  pillarLabel: string;
  asset: RoadmapAsset;
  priority: ConstraintPriority;
  priorityLabel: string;
  reason: string;
  /**
   * What the builder already said about this move, as the STORED result recorded it, or null
   * while it is open. A settled constraint is still shown — it is what is holding the business
   * back — but nothing may call it today's next move (22 Sep 2026). Absent on an older result.
   */
  response?: MoveResponseState | null;
}

export interface PrimeScore {
  assetHealthPoints: number; // 0–600
  assetHealthMax: number;
  kpiHealthPoints: number; // 0–400
  kpiHealthMax: number;
  bpsTotal: number; // 0–1000
  bpsMax: number;
  lifecycleStage: string;
  lifecycleLabel: string;
  pillarScores: Record<PillarCode, number>;
}

export interface PrimeScoreRoadmap {
  score: PrimeScore;
  pillars: PillarRoadmap[];
  constraint: Constraint | null;
}
